import { useDrop } from 'react-dnd';

import classNames from 'classnames';

import type { SavedSearchGroupItem } from '@iconik-io/iconik-api/src/schema_patched/saved_search';
import { useDndRef } from '@iconik-io/shared-lib/src/react/hooks/use_dnd_ref';

import ArrowOpener from '../../components/arrow_opener/arrow_opener';
import DraggableTypes from '../../constants/draggable_types';
import type { FCC } from '../../types/common';
import IconButton from '../../ui/icon_button/icon_button';
import OpenContextMenuButton from '../../ui/table/open_context_menu_button/open_context_menu_button';

import styles from './saved_search_group.module.css';

type Props = {
  group: SavedSearchGroupItem;
  isSelected: boolean;
  isCollapsed: boolean;
  onDrop: () => void;
  onExpand: (group: SavedSearchGroupItem) => void;
  onCollapse: (group: SavedSearchGroupItem) => void;
};

const SavedSearchGroup: FCC<Props> = (props) => {
  const { group, isSelected, isCollapsed, children } = props;

  const [collectedProps, dropRef] = useDrop(
    () => ({
      accept: DraggableTypes.SAVED_SEARCH,
      drop: () => {
        props.onDrop();
      },
      collect: (monitor) => ({ isDragOver: monitor.isOver() }),
    }),
    [props.onDrop],
  );

  const dropTargetRef = useDndRef(dropRef);

  const handleToggleExpand = () =>
    isCollapsed ? props.onExpand(group) : props.onCollapse(group);

  return (
    <div
      ref={dropTargetRef}
      role="option"
      aria-label={group.name}
      aria-selected={isSelected}
      tabIndex={0}
    >
      <div
        className={classNames(styles.root, {
          [styles.selected]: isSelected,
          [styles.drag_over]: collectedProps.isDragOver,
        })}
        data-id={group.id}
      >
        <ArrowOpener
          className={styles.group_collapsing_button}
          open={!isCollapsed}
          onClick={handleToggleExpand}
          aria={{
            label: `${isCollapsed ? 'Expand' : 'Collapse'} group ${group.name}`,
          }}
        />
        <IconButton
          iconName="collection"
          className={styles.group_icon}
          onClick={handleToggleExpand}
          aria={{
            hidden: true,
          }}
        />
        <div className={styles.name}>{group.name}</div>
        <OpenContextMenuButton />
      </div>
      <div
        className={classNames(styles.children_container, {
          [styles.collapsed_children]: isCollapsed,
        })}
      >
        {children}
      </div>
    </div>
  );
};

export default SavedSearchGroup;
